import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../services/api';

export interface Designation {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  permissions?: { module: string; actions: string[] }[];
}

interface WorkspaceState {
  active: boolean;
  designationId: string | null;
  designationName: string | null;
  permissions: string[];        // flat: ["module:action", ...]
  dashboardRoute: string;
  allDesignations: Designation[];
  loadingDesignations: boolean;
  loadingPermissions: boolean;
}

const STORAGE_KEY = 'w4t_workspace';

const loadPersisted = (): Partial<WorkspaceState> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw);
  } catch { return {}; }
};

const persisted = loadPersisted();

const initialState: WorkspaceState = {
  active: persisted.active ?? false,
  designationId: persisted.designationId ?? null,
  designationName: persisted.designationName ?? null,
  permissions: persisted.permissions ?? [],
  dashboardRoute: persisted.dashboardRoute ?? '/dashboard',
  allDesignations: [],
  loadingDesignations: false,
  loadingPermissions: false,
};

// ─── Dashboard route inference ──────────────────────────────────────────────
// Returns the /dashboard/* hub route for a given designation name + permissions.
// Used by workspace switcher (Super Admin entering a designation workspace).
// For login-time redirect see DashboardRedirect.tsx which also checks user.role.
export function inferDashboardRoute(name: string, permissions: string[]): string {
  const n = name.toLowerCase().trim().replace(/[\s\-]+/g, '_');

  if (n.includes('branch_manager'))  return '/erp/bm/dashboard';

  if ((n.includes('technician') || n.includes('field_tech')) &&
      !n.includes('technical_support') && !n.includes('tech_support')) return '/dashboard/technician';

  if (n.includes('backend_support')) return '/backend-support';
  if (n.includes('technical_support') || n.includes('tech_support') || n.includes('support_executive')) return '/dashboard/technical-support';

  if (n.includes('technician_manager') || n.includes('tm_manager') ||
      n.includes('service_manager')    || n.includes('installation_manager')) return '/dashboard/technician';

  if (n.includes('sales') || n.includes('marketing') || n.includes('telecaller') ||
      n.includes('collection_executive') || n.includes('dealer')) return '/dashboard/sales';

  if (n.includes('customer_support') || n.includes('crm')) return '/dashboard/crm';

  if (n.includes('finance') || n.includes('accountant') ||
      n.includes('accounts_manager') || n.includes('auditor')) return '/dashboard/finance';

  if (n.includes('warehouse') || n.includes('inventory') ||
      n.includes('procurement') || n.includes('erp')) return '/dashboard/warehouse';

  if (n.includes('hr') || n.includes('human_resource') || n.includes('recruiter') ||
      n.includes('payroll') || n.includes('department_manager') || n.includes('team_lead') ||
      n.includes('it_admin') || n.includes('director') || n.includes('ceo') ||
      n.includes('operations_manager')) return '/dashboard/hr';

  // Permission-based fallback
  if (permissions.some(p => p.startsWith('erp_bm_dashboard'))) return '/erp/bm/dashboard';
  if (permissions.some(p => p.startsWith('tm_manager')))       return '/dashboard/technician';
  if (permissions.some(p => p.startsWith('tm_dashboard')))     return '/dashboard/technician';
  if (permissions.some(p => p.startsWith('sm_dashboard')))     return '/dashboard/sales';
  if (permissions.some(p => p.startsWith('fa_dashboard')))     return '/dashboard/finance';
  if (permissions.some(p => p.startsWith('crm_dashboard')))    return '/dashboard/crm';
  if (permissions.some(p => p.startsWith('ts_dashboard')))     return '/dashboard/technical-support';
  if (permissions.some(p => p.startsWith('erp_dashboard')))    return '/dashboard/warehouse';

  return '/dashboard/employee';
}

// ─── Thunks ──────────────────────────────────────────────────────────────────
export const fetchAllDesignations = createAsyncThunk('workspace/fetchAll', async () => {
  const res = await api.get('/designations');
  return (res.data.data || []) as Designation[];
});

export const enterWorkspace = createAsyncThunk(
  'workspace/enter',
  async (designationId: string) => {
    const res = await api.get(`/designations/${designationId}/permissions-flat`);
    const { designationId: id, designationName, permissions } = (res.data.data ?? res.data) as {
      designationId: string; designationName: string; permissions: string[];
    };
    const dashboardRoute = inferDashboardRoute(designationName, permissions);
    return { designationId: id, designationName, permissions: permissions ?? [], dashboardRoute };
  },
);

// ─── Slice ───────────────────────────────────────────────────────────────────
const workspaceSlice = createSlice({
  name: 'workspace',
  initialState,
  reducers: {
    exitWorkspace(state) {
      state.active = false;
      state.designationId = null;
      state.designationName = null;
      state.permissions = [];
      state.dashboardRoute = '/dashboard';
      localStorage.removeItem(STORAGE_KEY);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllDesignations.pending, (state) => { state.loadingDesignations = true; })
      .addCase(fetchAllDesignations.fulfilled, (state, action) => {
        state.allDesignations = action.payload;
        state.loadingDesignations = false;
      })
      .addCase(fetchAllDesignations.rejected, (state) => { state.loadingDesignations = false; })

      .addCase(enterWorkspace.pending, (state) => { state.loadingPermissions = true; })
      .addCase(enterWorkspace.fulfilled, (state, action) => {
        const { designationId, designationName, permissions, dashboardRoute } = action.payload;
        state.active = true;
        state.designationId = designationId;
        state.designationName = designationName;
        state.permissions = permissions;
        state.dashboardRoute = dashboardRoute;
        state.loadingPermissions = false;
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          active: true, designationId, designationName, permissions, dashboardRoute,
        }));
      })
      .addCase(enterWorkspace.rejected, (state) => { state.loadingPermissions = false; });
  },
});

export const { exitWorkspace } = workspaceSlice.actions;
export default workspaceSlice.reducer;
